import {
  extendPromise,
  type Category,
  type FetchProductsByCategoryParams,
  APISortOption,
  APISortOrder,
  type Product,
} from '@scayle/storefront-nuxt'
import { type MaybeRefOrGetter, computed, toValue, watch, ref } from 'vue'

import { PRODUCT_TILE_WITH_PARAMS } from '~/constants/withParams'
import {
  PRODUCTS_PER_PAGE,
  FETCH_PRODUCTS_CACHE_TTL,
} from '~/constants/product'
import { useRoute } from '#app/composables/router'
import {
  useAppliedFilters,
  useProductListSort,
} from '#storefront-product-listing'
import { useProducts } from '#storefront/composables'
import { isDataFetching } from '~/utils'
import type { ComputedRef } from '#imports'

export function useProductsByCategory(
  category: MaybeRefOrGetter<Category | undefined | null>,
  {
    params: {
      includeSoldOut = true,
      includeSellableForFree = true,
      pricePromotionKey = '',
      orFiltersOperator,
      ...restParams
    } = {},
    options,
  }: Partial<{
    params: Omit<
      Partial<FetchProductsByCategoryParams>,
      'category' | 'categoryId'
    >
    options: Partial<{ lazy: boolean; immediate: boolean }>
  }> = {},
) {
  const route = useRoute()
  const currentCategoryId = computed(
    () => Number(route.params?.id) || toValue(category)?.id,
  )
  const lastDataCategoryId = ref<string | number | undefined>(undefined)
  const CUSTOM_SORTING_KEY = computed(() => {
    return {
      key: `${
        toValue(category)?.countryLevelCustomData.sortingKey || 'top_seller'
      }`,
      sortingKey: `${
        toValue(category)?.countryLevelCustomData.sortingKey ||
        'scayle:v1:top_seller'
      }`,
      direction: (toValue(category)?.countryLevelCustomData
        .sortingKey as string)
        ? APISortOrder.ASCENDING
        : APISortOrder.DESCENDING,
    }
  })

  const { selectedSort } = useProductListSort(route, {
    sortingOptions: [
      {
        key: CUSTOM_SORTING_KEY.value.key,
        sortingKey: CUSTOM_SORTING_KEY.value.sortingKey,
        direction: CUSTOM_SORTING_KEY.value.direction,
        label: 'sorting_select.top_seller',
      },
      {
        key: 'date_newest',
        by: APISortOption.DATE_ADDED,
        direction: APISortOrder.DESCENDING,
        label: 'sorting_select.date_newest',
      },
      {
        key: 'price_desc',
        by: APISortOption.PRICE,
        direction: APISortOrder.DESCENDING,
        label: 'sorting_select.price_desc',
      },
      {
        key: 'price_asc',
        by: APISortOption.PRICE,
        direction: APISortOrder.ASCENDING,
        label: 'sorting_select.price_asc',
      },
      {
        key: 'reduction_desc',
        by: APISortOption.REDUCTION,
        direction: APISortOrder.DESCENDING,
        label: 'sorting_select.reduction_desc',
      },
    ],
    defaultSortingKey: CUSTOM_SORTING_KEY.value.sortingKey,
  })
  const { appliedFilter } = useAppliedFilters(route)

  // Ensure SSR fetch (no lazy) and immediate run by default
  const normalizedOptions = {
    lazy: false,
    immediate: true,
    ...(options || {}),
    // On server, force fetch now
    ...(import.meta.server ? { lazy: false, immediate: true } : {}),
  }

  const productsData = useProducts(
    {
      params: () => ({
        ...(route.query.page && { page: +route.query.page }),
        sort: selectedSort.value,
        perPage: Number(route.query.products_per_page) || PRODUCTS_PER_PAGE,
        with: PRODUCT_TILE_WITH_PARAMS,
        categoryId: currentCategoryId.value,
        includeSoldOut,
        includeSellableForFree,
        pricePromotionKey,
        orFiltersOperator,
        cache: {
          ttl: FETCH_PRODUCTS_CACHE_TTL,
          cacheKeyPrefix: `PLP:${currentCategoryId.value}`,
        },
        where: {
          ...appliedFilter.value,
        },
        ...restParams,
      }),
      options: normalizedOptions,
    },
    `${currentCategoryId.value}-products`,
  )

  const { data, status, error } = productsData
  const fetching = computed(() => isDataFetching(status))

  // Keep an aggregated list of products across page changes
  const aggregatedProducts = ref<Product[]>([])
  const loadedPages = ref(new Set<number>())

  const currentPage = computed(
    () =>
      data.value?.pagination?.page ??
      (route.query.page ? +route.query.page : 1),
  )
  const perPage = computed(
    () =>
      data.value?.pagination?.perPage ??
      (Number(route.query.products_per_page) || PRODUCTS_PER_PAGE),
  )

  function dedupeById(list: any[]) {
    const seen = new Set<any>()
    const out: any[] = []
    for (const item of list) {
      const id =
        (item && (item.id ?? item.productId ?? item.sku)) ??
        JSON.stringify(item)
      if (seen.has(id)) {
        continue
      }
      seen.add(id)
      out.push(item)
    }
    return out
  }

  // Reset aggregation when category/sort/filters/perPage change (but NOT when only page changes)
  watch(
    [
      () => currentCategoryId.value,
      () => JSON.stringify(selectedSort.value ?? {}),
      () => JSON.stringify(appliedFilter.value ?? {}),
      () => perPage.value,
    ],
    () => {
      aggregatedProducts.value = []
      loadedPages.value = new Set<number>()
      lastDataCategoryId.value = undefined
    },
    { immediate: import.meta.server },
  )

  // Merge newly fetched page into aggregated list when `useProducts` delivers new data
  watch(
    [() => currentPage.value, () => data.value?.products],
    ([page, list]) => {
      if (!Array.isArray(list)) {
        return
      }

      const catId = currentCategoryId.value

      // If the category changed since last aggregation, replace with fresh data
      if (lastDataCategoryId.value !== catId) {
        aggregatedProducts.value = list.slice()
        loadedPages.value = new Set<number>([page as number])
        lastDataCategoryId.value = catId
        return
      }

      // first load (same category)
      if (loadedPages.value.size === 0) {
        aggregatedProducts.value = list.slice()
        loadedPages.value.add(page as number)
        return
      }

      if (loadedPages.value.has(page as number)) {
        // page already incorporated, skip
        return
      }

      // Decide whether to append (next) or prepend (previous) by comparing page numbers
      const minLoaded = Math.min(...Array.from(loadedPages.value))
      const maxLoaded = Math.max(...Array.from(loadedPages.value))

      if ((page as number) === maxLoaded + 1) {
        // append next page
        aggregatedProducts.value = dedupeById(
          aggregatedProducts.value.concat(list),
        )
      } else if ((page as number) === minLoaded - 1) {
        // prepend previous page
        aggregatedProducts.value = dedupeById(
          list.concat(aggregatedProducts.value),
        )
      } else {
        // Non-consecutive jump: replace with the new page only
        aggregatedProducts.value = list.slice()
        loadedPages.value = new Set<number>([page as number])
      }

      loadedPages.value.add(page as number)
    },
    { immediate: true },
  )

  const products = computed(() => aggregatedProducts.value)
  const productsCount = computed(() => aggregatedProducts.value.length)
  const pagination = computed(() => data.value?.pagination)

  const minLoadedPage = computed(() =>
    loadedPages.value.size
      ? Math.min(...Array.from(loadedPages.value))
      : currentPage.value,
  )

  const maxLoadedPage = computed(() =>
    loadedPages.value.size
      ? Math.max(...Array.from(loadedPages.value))
      : currentPage.value,
  )

  const prevPageToLoad: ComputedRef<number | null> = computed(() => {
    const prev = minLoadedPage.value - 1
    return prev >= 1 ? prev : null
  })

  const nextPageToLoad: ComputedRef<number | null> = computed(() => {
    const canGoNext =
      (data.value?.pagination?.page ?? null) !== null &&
      (data.value?.pagination?.next ?? null) !== null &&
      (data.value?.pagination?.last ?? null) !== null &&
      (data.value?.pagination!.page as number) <
        (data.value?.pagination!.next as number) &&
      (data.value?.pagination!.next as number) <=
        (data.value?.pagination!.last as number) &&
      (data.value?.pagination!.last as number) >= maxLoadedPage.value + 1
    const hasNext = Boolean(data.value?.pagination?.next)
    return hasNext && canGoNext ? maxLoadedPage.value + 1 : null
  })

  const totalProductsCount = computed(() => data.value?.pagination.total ?? 0)

  const paginationOffset = computed(() => {
    const page = data.value?.pagination.page ?? 1
    const perPage = data.value?.pagination.perPage ?? PRODUCTS_PER_PAGE
    return (page - 1) * perPage
  })

  return extendPromise(
    productsData.then(() => ({})),
    {
      products,
      pagination,
      error,
      fetching,
      totalProductsCount,
      productsCount,
      loadedPages,
      paginationOffset,
      prevPageToLoad,
      nextPageToLoad,
    },
  )
}
